import { useEffect, useRef, useState, type FormEvent } from 'react'

import { API_BASE, errorMessage } from '@/api/client'
import {
  CORPUS_TABLE_LABELS,
  CORPUS_TABLES,
  type CorpusTable,
  type Provision,
  type ProvisionSummary,
  useArguments,
  useAuthorities,
  useDecisions,
  useFlow,
  useGroundCoverage,
  useGroundFindings,
  useLessons,
  useNiceClasses,
  useOfficeCoverage,
  usePatterns,
  usePhrases,
  useProvision,
  useProvisions,
} from '@/api/corpusIntel'
import { useAskCorpus } from '@/api/ask'
import { useSuggestNiceClasses } from '@/api/niceClassifier'
import { cleanGrounds } from '@/api/pipeline'
import { useMatterLedger } from '@/api/pipeline'
import { useRegisteredQueries, useStatQuery } from '@/api/stats'
import type {
  AskCitation,
  AskRejectedCitation,
  Jurisdiction,
  RegisteredQuery,
  SuggestNiceClassResponse,
} from '@/api/types'
import { JURISDICTIONS, jurisdictionLabel } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  ScreenHeader,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
  cn,
} from '@/components/ui'
import { plainText } from '@/plainText'

/**
 * Intelligence — chunk 9, the docket's own words: "React screens over the
 * corpus API: search across all eight drafting tables (not just law),
 * ask-with-citations, registered statistics (I9 caveats in-payload), the
 * ledger view." — plus, later, Decisions and Nice classes alongside them.
 *
 * Four workspaces, each answering one piece of the docket's own acceptance
 * line — *"an analyst can answer 'what do we argue for 2(d) and how often
 * does it work' without SQL, and every number carries its caveat"*:
 *
 * | Workspace | Answers | Reuses |
 * |---|---|---|
 * | Corpus | what do we argue, what phrases, what happened, what has this office actually decided, which class fits this description | `corpus_law.py` (law/authorities) + `corpus_intel.py` (the other eight) + `nice_classifier.py` |
 * | Ask | a free-text question, answered from the corpus, every claim quoted and verified | `services/ask.py` — new this chunk |
 * | Statistics | how often it works, with its caveat | `stats.py` — built earlier, unwired until now |
 * | Ledger | which document backs a given matter's runs | chunk 6's per-run ledger, extended cross-run |
 *
 * ## What is deliberately not built
 *
 * **URL state.** Chunk 1's law explorer round-trips every filter through
 * `location.hash` so a link opens on the exact provision a colleague meant.
 * This screen does not — it already lives at `#/intelligence`, a hash the
 * app's own router owns, and layering a second hash-encoded state machine
 * inside one route is out of scope for this pass. Component state is lost on
 * reload; that is a real, named narrowing, not an oversight.
 *
 * **The law reading pane is now here, not a new tab.** The Corpus
 * workspace's Law tab opens a provision in-app — `LawPane` below, calling
 * the same `GET /api/corpus/law/provision` the server-rendered explorer at
 * `/api/corpus/law/explorer` was built over — rather than sending an analyst
 * out of the shell to read one. The explorer itself is retired: `routes.ts`'s
 * `EXTERNAL_LINKS` no longer names it, and this pane deliberately keeps that
 * file's one hard rule intact — a provision is read whole, untagged chunks
 * included, never paginated chunk by chunk, because a filtered read
 * completing a provision's missing tail from memory is the exact failure
 * `docs/SKILL_RESPONSE.md` records. "Prev/next" here moves between whole
 * provisions for the same reason.
 *
 * **Register fetch.** The docket's third bullet — "fetch door starts here
 * when API keys land" — is out of scope for the whole build (no fetch API
 * key exists in this environment); every table here reads the corpus already
 * loaded by `load_fixtures`/`load_corpus`, never a live registry.
 */
export function IntelligenceScreen() {
  const { hasScope } = useAuth()
  const [workspace, setWorkspace] = useState<'corpus' | 'ask' | 'stats' | 'ledger'>('corpus')

  if (!hasScope('research:read')) {
    return (
      <EmptyState
        title="research:read required"
        description="Every workspace on this screen reads corpus material behind the research:read scope, which this credential does not hold."
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        title="Intelligence"
        description="Search across the corpus, ask a question with verified citations, read the registered statistics, and trace a matter's citation ledger."
      />

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Workspace">
        {(
          [
            ['corpus', 'Law library'],
            ['ask', 'Ask a question'],
            ['stats', 'Statistics'],
            ['ledger', 'Citations'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={workspace === key}
            onClick={() => setWorkspace(key)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-meta font-medium',
              workspace === key
                ? 'border-brand-700 bg-brand-50 text-brand-800'
                : 'border-divider bg-surface text-muted hover:border-brand-300',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {workspace === 'corpus' ? <CorpusWorkspace /> : null}
      {workspace === 'ask' ? <AskWorkspace /> : null}
      {workspace === 'stats' ? <StatisticsWorkspace /> : null}
      {workspace === 'ledger' ? <LedgerWorkspace /> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Corpus — search across all ten drafting tables
// ---------------------------------------------------------------------------

/** Tables with no `ground` concept of their own — `flow` is an aggregate
 * keyed on `(office, doc_role, move)`, never a ground; `nice_classes` is
 * treaty reference data with no ground either (see `OFFICELESS_TABLES`). */
const GROUNDLESS_TABLES = new Set<CorpusTable>(['flow', 'nice_classes'])

/** Tables with no `office` concept of their own. The Nice Classification is
 * a multilateral schedule, identical across every office this product
 * covers — `TmNiceClass`'s own docstring gives the same reasoning the
 * `office` filter chips would otherwise apply to a column that does not
 * exist on this table. */
const OFFICELESS_TABLES = new Set<CorpusTable>(['nice_classes'])

function CorpusWorkspace() {
  const [office, setOffice] = useState<string | null>(null)
  const [ground, setGround] = useState<string | null>(null)
  const [table, setTable] = useState<CorpusTable>('law')

  const offices = useOfficeCoverage(true)
  const grounds = useGroundCoverage(office, !GROUNDLESS_TABLES.has(table))

  function pickOffice(next: string | null) {
    setOffice(next)
    setGround(null)
  }

  return (
    <Card role="group" aria-label="Law library" className="gap-4">
      <CardHeader
        title="Law library"
        description="Every drafting table the corpus holds, filtered by office and ground. Provenance is one click away on every row that has a document behind it."
      />

      {!OFFICELESS_TABLES.has(table) ? (
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Office">
          <FilterChip active={office === null} onClick={() => pickOffice(null)}>
            all offices
          </FilterChip>
          {(offices.data ?? []).map((row) => (
            <FilterChip key={row.office} active={office === row.office} onClick={() => pickOffice(row.office)}>
              {row.office} · {row.chunks}
            </FilterChip>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Table">
        {CORPUS_TABLES.map((key) => (
          <FilterChip key={key} active={table === key} onClick={() => setTable(key)}>
            {CORPUS_TABLE_LABELS[key]}
          </FilterChip>
        ))}
      </div>

      {!GROUNDLESS_TABLES.has(table) ? (
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Ground">
          <FilterChip active={ground === null} onClick={() => setGround(null)}>
            all grounds
          </FilterChip>
          {(grounds.data ?? []).map((row) => (
            <FilterChip key={row.ground} active={ground === row.ground} onClick={() => setGround(row.ground)}>
              {plainText(row.ground)}
            </FilterChip>
          ))}
        </div>
      ) : null}

      <CorpusTablePane table={table} office={office} ground={ground} />
    </Card>
  )
}

/**
 * Why a registered statistic declined to give a rate, in words.
 *
 * The panel printed `Refused: no_data` — a code, for the one situation that is
 * most often a gap in the data and not a finding (TM-03: only one office had
 * decisions loaded, so every other office read as refused). Both messages say
 * what is true: a refusal is never a rate of zero.
 */
function refusalCopy(reason: string | null | undefined, caveat: string): string {
  if (reason === 'no_data') {
    return 'No rate: there are no decisions on record for this office and ground, so there is nothing to count. That is a gap in the data we hold, not a rate of zero.'
  }
  if (reason === 'quota_sample') {
    return 'No rate: the only data held for this is a sample we assembled, not every decision, so a share of it would not describe how this office decides.'
  }
  return `Refused: ${reason ?? caveat}`
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-micro',
        active ? 'border-brand-700 bg-brand-50 text-brand-800' : 'border-divider bg-surface text-muted',
      )}
    >
      {children}
    </button>
  )
}

/** An "open source" link, identical in shape to `CaseScreen`'s `LedgerCard` —
 * one definition of "does this link open" (`/api/source/{custom_id}`),
 * reused everywhere a row carries a document behind it (I2 / REQ-21). */
function OpenSource({ customId }: { customId: string | null }) {
  if (!customId) return <span className="text-muted">—</span>
  return (
    <a
      href={`${API_BASE}/api/source/${encodeURIComponent(customId)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-micro text-brand-700 no-underline hover:underline"
    >
      open source
    </a>
  )
}

function CorpusTablePane({
  table,
  office,
  ground,
}: {
  table: CorpusTable
  office: string | null
  ground: string | null
}) {
  const authorities = useAuthorities(office, ground, table === 'authorities')
  const args = useArguments(office, ground, table === 'arguments')
  const phrases = usePhrases(office, ground, table === 'phrases')
  const flow = useFlow(office, table === 'flow')
  const findings = useGroundFindings(office, ground, table === 'ground_findings')
  const patterns = usePatterns(office, ground, table === 'patterns')
  const lessons = useLessons(office, ground, table === 'lessons')

  switch (table) {
    case 'law':
      return <LawPane office={office} ground={ground} />
    case 'decisions':
      return <DecisionsPane office={office} ground={ground} />
    case 'nice_classes':
      return <NiceClassesPane />
    case 'authorities':
      return (
        <EmptyRows loading={authorities.isPending} error={authorities.error} count={authorities.data?.authorities.length ?? 0}>
          <CappedNote total={authorities.data?.total} capped={authorities.data?.capped} />
          <Table>
            <THead>
              <TR>
                <TH>Citation</TH>
                <TH>Proposition</TH>
                <TH>Office</TH>
                <TH>Ground</TH>
                <TH align="right">Cited</TH>
              </TR>
            </THead>
            <TBody>
              {(authorities.data?.authorities ?? []).map((row) => (
                <TR key={row.id}>
                  <TD>
                    <span className="font-mono text-meta">{row.case_name ?? row.citation}</span>
                  </TD>
                  <TD>{row.proposition ?? '—'}</TD>
                  <TD>{row.office ?? '—'}</TD>
                  <TD>{plainText(row.ground) ?? '—'}</TD>
                  <TD align="right">{row.n_citations ?? 0}×</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
    case 'arguments':
      return (
        <EmptyRows loading={args.isPending} error={args.error} count={args.data?.arguments.length ?? 0}>
          <CappedNote total={args.data?.total} capped={args.data?.capped} />
          <Table>
            <THead>
              <TR>
                <TH>Proposition</TH>
                <TH>Type</TH>
                <TH>Side</TH>
                <TH>Outcome</TH>
                <TH>Ground</TH>
                <TH>Source</TH>
              </TR>
            </THead>
            <TBody>
              {(args.data?.arguments ?? []).map((row) => (
                <TR key={row.id}>
                  <TD>{row.proposition}</TD>
                  <TD>{row.arg_type ?? '—'}</TD>
                  <TD>{row.side ?? '—'}</TD>
                  <TD>
                    {row.outcome_tag ? <Badge tone="neutral">{row.outcome_tag}</Badge> : '—'}
                    {row.inferred ? (
                      <Badge tone="warn" className="ml-1">
                        inferred
                      </Badge>
                    ) : null}
                  </TD>
                  <TD>{plainText(row.ground)}</TD>
                  <TD>
                    <OpenSource customId={row.custom_id} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
    case 'phrases':
      return (
        <EmptyRows loading={phrases.isPending} error={phrases.error} count={phrases.data?.phrases.length ?? 0}>
          <CappedNote total={phrases.data?.total} capped={phrases.data?.capped} />
          <p className="m-0 text-micro text-muted">
            House phrases — internal only. They shape how a draft reads; they are never quoted to a
            client as authority.
          </p>
          <Table>
            <THead>
              <TR>
                <TH>Phrase</TH>
                <TH>Move</TH>
                <TH>Ground</TH>
                <TH align="right">Uses</TH>
                <TH>Source</TH>
              </TR>
            </THead>
            <TBody>
              {(phrases.data?.phrases ?? []).map((row) => (
                <TR key={row.id}>
                  <TD>&ldquo;{row.phrase}&rdquo;</TD>
                  <TD>{row.move ?? '—'}</TD>
                  <TD>{plainText(row.ground) ?? '—'}</TD>
                  <TD align="right">{row.n_uses ?? '—'}</TD>
                  <TD>
                    <OpenSource customId={row.custom_id} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
    case 'flow':
      return (
        <EmptyRows loading={flow.isPending} error={flow.error} count={flow.data?.steps.length ?? 0}>
          <Table>
            <THead>
              <TR>
                <TH>Move</TH>
                <TH>Doc role</TH>
                <TH align="right">Avg. position</TH>
                <TH align="right">% of documents</TH>
                <TH align="right">n</TH>
              </TR>
            </THead>
            <TBody>
              {(flow.data?.steps ?? []).map((row) => (
                <TR key={`${row.doc_role}:${row.move}`}>
                  <TD>{row.move}</TD>
                  <TD>{row.doc_role}</TD>
                  <TD align="right">{row.avg_position.toFixed(2)}</TD>
                  <TD align="right">{(row.pct_of_documents * 100).toFixed(0)}%</TD>
                  <TD align="right">{row.n_documents ?? '—'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
    case 'ground_findings':
      return (
        <EmptyRows loading={findings.isPending} error={findings.error} count={findings.data?.findings.length ?? 0}>
          <CappedNote total={findings.data?.total} capped={findings.data?.capped} />
          <p className="m-0 text-micro text-muted">
            <code>succeeded</code> means the objection succeeded — bad for the applicant.
          </p>
          <Table>
            <THead>
              <TR>
                <TH>Ground</TH>
                <TH>Result</TH>
                <TH>Reason</TH>
                <TH>Source</TH>
              </TR>
            </THead>
            <TBody>
              {(findings.data?.findings ?? []).map((row) => (
                <TR key={row.id}>
                  <TD>{plainText(row.ground)}</TD>
                  <TD>
                    <Badge tone={row.result === 'succeeded' ? 'block' : 'ok'}>{row.result}</Badge>
                  </TD>
                  <TD>{row.reason ?? '—'}</TD>
                  <TD>
                    <OpenSource customId={row.custom_id} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
    case 'patterns':
      return (
        <EmptyRows loading={patterns.isPending} error={patterns.error} count={patterns.data?.patterns.length ?? 0}>
          <CappedNote total={patterns.data?.total} capped={patterns.data?.capped} />
          <Table>
            <THead>
              <TR>
                <TH>Argument type</TH>
                <TH>Ground</TH>
                <TH align="right">n</TH>
                <TH>Exemplar</TH>
                <TH>Source</TH>
              </TR>
            </THead>
            <TBody>
              {(patterns.data?.patterns ?? []).map((row) => (
                <TR key={row.id}>
                  <TD>{row.arg_type}</TD>
                  <TD>{plainText(row.ground)}</TD>
                  <TD align="right">{row.n_arguments}</TD>
                  <TD>{row.exemplar ?? '—'}</TD>
                  <TD>
                    <OpenSource customId={row.exemplar_source} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
    case 'lessons':
      return (
        <EmptyRows loading={lessons.isPending} error={lessons.error} count={lessons.data?.lessons.length ?? 0}>
          <CappedNote total={lessons.data?.total} capped={lessons.data?.capped} />
          <Table>
            <THead>
              <TR>
                <TH>Lesson</TH>
                <TH>Ground</TH>
                <TH>Source</TH>
              </TR>
            </THead>
            <TBody>
              {(lessons.data?.lessons ?? []).map((row) => (
                <TR key={row.id}>
                  <TD>{row.lesson}</TD>
                  <TD>{plainText(row.ground) ?? '—'}</TD>
                  <TD>
                    <OpenSource customId={row.custom_id} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      )
  }
}

// ---------------------------------------------------------------------------
// Law — the in-app reading pane, replacing the server-rendered explorer tab
// ---------------------------------------------------------------------------

/** A provision's identity in the list, independent of where it currently sits
 * in it. `office` is part of it because `citation` alone is not unique across
 * offices — the same section number exists under more than one statute. */
function provisionKey(row: ProvisionSummary): string {
  return `${row.office ?? ''}:${row.citation}`
}

function LawPane({ office, ground }: { office: string | null; ground: string | null }) {
  const provisions = useProvisions(office, ground, true)
  // The open provision is tracked by identity, never by its index in `rows`.
  // `rows` is refetched whenever the office/ground chips change, and an index
  // into the old list addresses a different provision in the new one — so
  // filtering while the pane was open used to silently swap the reader onto an
  // unrelated statute (or blank the pane when the new list was shorter) with
  // no visible transition. A pane whose entire purpose is "you are reading
  // this exact provision, whole" cannot be addressed by position.
  const [openKey, setOpenKey] = useState<string | null>(null)
  const rows = provisions.data ?? []
  const openIndex = openKey === null ? -1 : rows.findIndex((row) => provisionKey(row) === openKey)

  return (
    <div className="flex flex-col gap-2">
      <EmptyRows loading={provisions.isPending} error={provisions.error} count={rows.length}>
        <Table>
          <THead>
            <TR>
              <TH>Citation</TH>
              <TH>Heading</TH>
              <TH>Office</TH>
              <TH>Chunks</TH>
              <TH>Grounds</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={provisionKey(row)}>
                <TD>
                  <button
                    type="button"
                    onClick={() => setOpenKey(provisionKey(row))}
                    className="cursor-pointer bg-transparent p-0 font-mono text-meta text-brand-700 underline decoration-dotted hover:decoration-solid"
                  >
                    {row.citation}
                  </button>
                </TD>
                <TD>{row.heading ?? '—'}</TD>
                <TD>{row.office ?? '—'}</TD>
                <TD>
                  {row.tagged}/{row.chunks} tagged
                </TD>
                <TD>{row.grounds.join(', ') || '—'}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </EmptyRows>

      {openIndex >= 0 ? (
        <ProvisionReadingPane
          provisions={rows}
          index={openIndex}
          onNavigate={(next) => {
            const row = rows[next]
            if (row) setOpenKey(provisionKey(row))
          }}
          onClose={() => setOpenKey(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * One provision, whole, in an in-app panel — the actual read the Law tab's
 * list exists to lead to.
 *
 * Prev/next moves between whole *provisions* in the list this pane was
 * opened from, never between a provision's own chunks: `GET /provision` is
 * deliberately the never-truncated read — every chunk, untagged tail
 * included, in reading order — and paginating it would reintroduce the
 * exact hazard that route exists to close (the first live draft completed
 * s.11(1)'s missing tail from memory when a filtered read had silently
 * withheld it). See `corpus_law.py`'s own explorer JS, which this pane
 * ports the same discipline from — same route, same rule, an in-app reader
 * instead of a page this shell had no way back from.
 */
function ProvisionReadingPane({
  provisions,
  index,
  onNavigate,
  onClose,
}: {
  provisions: ProvisionSummary[]
  index: number
  onNavigate: (index: number) => void
  onClose: () => void
}) {
  const row = provisions[index]
  // Only reachable if `onNavigate` is ever called with an index the list this
  // pane was opened from does not have — never true for LawPane's own use of
  // this component, and guarded here rather than assumed away so the type
  // stays honest about what indexing an array actually returns.
  const provision = useProvision(row?.office ?? null, row?.citation ?? null, row !== undefined)
  const [copyLabel, setCopyLabel] = useState('Copy citation')
  const panelRef = useRef<HTMLDivElement>(null)
  const hasPrev = index > 0
  const hasNext = index < provisions.length - 1

  // Move focus into the panel when it opens. It renders *below* the table it
  // was opened from, so without this a keyboard or screen-reader user has to
  // tab back through every remaining row to reach what their click just
  // opened — and nothing announces that a panel appeared at all.
  useEffect(() => {
    panelRef.current?.focus()
  }, [])

  if (!row) return null
  const citation = row.citation

  async function copyCitation() {
    try {
      await navigator.clipboard.writeText(citation)
      setCopyLabel('Copied')
    } catch {
      // Clipboard API can be unavailable (permissions, a non-secure
      // context) — the citation is still selectable text in the header
      // either way, so this degrades to a hint rather than a silent no-op.
      setCopyLabel('Select & copy')
    }
    setTimeout(() => setCopyLabel('Copy citation'), 1500)
  }

  return (
    // `region`, not `dialog`/`aria-modal`. This panel renders inline below the
    // table, traps no focus, and leaves the whole page interactive —
    // `aria-modal="true"` told assistive tech the opposite, hiding the
    // provisions table, the filter chips and the workspace tabs from a
    // screen-reader user while a sighted user could still click every one of
    // them. Escape is handled here rather than globally, so it only closes the
    // panel when focus is actually inside it.
    <div
      ref={panelRef}
      role="region"
      tabIndex={-1}
      aria-label={`Reading pane — ${row.citation}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose()
      }}
      className="flex flex-col gap-3 rounded-md border border-divider bg-surface p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-divider pb-3">
        <div>
          <h4 className="m-0 text-h3 font-semibold">{provision.data?.heading ?? row.heading ?? '(no heading)'}</h4>
          <p className="m-0 text-micro text-muted">
            {row.office ?? ''} · <code className="font-mono">{row.citation}</code>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={copyCitation}>
            {copyLabel}
          </Button>
          <Button size="sm" variant="secondary" disabled={!hasPrev} onClick={() => onNavigate(index - 1)}>
            &larr; prev
          </Button>
          <Button size="sm" variant="secondary" disabled={!hasNext} onClick={() => onNavigate(index + 1)}>
            next &rarr;
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {provision.isPending ? <p className="m-0 text-body text-muted">Loading…</p> : null}
      {provision.isError ? (
        <p role="alert" className="m-0 text-body text-block-fg">
          {errorMessage(provision.error)}
        </p>
      ) : null}
      {provision.data ? (
        <ProvisionBody provision={provision.data} />
      ) : null}
    </div>
  )
}

/**
 * The provision itself.
 *
 * A chunk the corpus holds with no stored text is drawn as a named gap rather
 * than an empty paragraph. That distinction is the point of the whole pane:
 * this read is what a reviewer checks a draft's law block against, so a chunk
 * that renders blank would read as "the statute says nothing here" when what
 * happened is that this deployment has no text for it.
 */
function ProvisionBody({ provision }: { provision: Provision }) {
  const missing = provision.missing_text.length

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-micro text-muted">grounds: {provision.grounds.join(', ') || '—'}</p>
      {missing > 0 ? (
        <p role="status" className="m-0 text-meta text-warn-fg">
          {missing} of {provision.chunks.length}{' '}
          {provision.chunks.length === 1 ? 'chunk' : 'chunks'} in this provision carry no text in
          this corpus. What is below is not the whole provision — read it against the office&rsquo;s
          own published text before relying on it.
        </p>
      ) : null}
      <div className="flex flex-col gap-3">
        {provision.chunks.map((chunk) => (
          <div
            key={chunk.id}
            className={cn(
              'rounded-md border px-3 py-2',
              chunk.ground ? 'border-divider bg-ground' : 'border-warn-line bg-warn-bg',
            )}
          >
            <p className="m-0 mb-1 text-micro text-muted">
              {chunk.ground ? (
                `ground ${plainText(chunk.ground)}`
              ) : (
                <span className="text-warn-fg">untagged — withheld by a grounds-filtered read</span>
              )}
              {chunk.page != null ? ` · p.${chunk.page}` : ''} ·{' '}
              <code className="font-mono">{chunk.custom_id ?? '(no source id)'}</code>
            </p>
            {chunk.chunk_text ? (
              <p className="m-0 whitespace-pre-wrap text-body">{chunk.chunk_text}</p>
            ) : (
              <p className="m-0 text-body text-warn-fg">
                No text stored for this chunk. It is listed because the provision contains it, and
                omitting it would make this read look complete.
              </p>
            )}
          </div>
        ))}
      </div>
      <AnalogousDecisions office={provision.office} ground={provision.grounds[0] ?? null} />
    </div>
  )
}

/** How many analogous decisions the reading pane shows inline. A companion to
 * the provision being read, not a substitute for the Decisions table — and
 * whenever it actually truncates, the pane says so. */
const ANALOGOUS_DECISIONS_SHOWN = 5

/**
 * Analogous past decisions, right where a reviewer is already reading the
 * law — the "analogs from past cases" the corpus's row-level decisions
 * lookup (`GET /api/corpus/decisions`) exists to answer, scoped to the
 * provision's own office and its first ground rather than making a reader
 * repeat the filter by hand.
 */
function AnalogousDecisions({ office, ground }: { office: string | null; ground: string | null }) {
  const decisions = useDecisions(office, ground, null, null, office !== null)
  if (!office) return null

  return (
    <div className="flex flex-col gap-2 border-t border-divider pt-3">
      <h5 className="m-0 text-meta font-semibold">
        Analogous decisions{ground ? ` — ${ground}` : ''}
      </h5>
      {decisions.isPending ? <p className="m-0 text-micro text-muted">Loading…</p> : null}
      {decisions.isError ? (
        <p role="alert" className="m-0 text-micro text-block-fg">
          {errorMessage(decisions.error)}
        </p>
      ) : null}
      {decisions.data && decisions.data.decisions.length === 0 ? (
        <p className="m-0 text-micro text-muted">No decisions on record for this office/ground.</p>
      ) : null}
      {decisions.data && decisions.data.decisions.length > 0 ? (
        <>
          <CappedNote total={decisions.data.total} capped={decisions.data.capped} />
          {/* The server's cap is reported by CappedNote above; this one is
              ours, and the no-silent-caps rule does not stop applying because
              the truncation happened in the browser. Without this line a
              ground with 40 decisions on record renders five and reads as
              all five there are — `capped` is false, so CappedNote says
              nothing, and the shortfall is invisible. */}
          {decisions.data.decisions.length > ANALOGOUS_DECISIONS_SHOWN ? (
            <p className="m-0 text-micro text-warn-fg">
              Showing the first {ANALOGOUS_DECISIONS_SHOWN} of {decisions.data.total} — open the
              Decisions table for the rest.
            </p>
          ) : null}
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {decisions.data.decisions.slice(0, ANALOGOUS_DECISIONS_SHOWN).map((d) => (
              <li key={d.id} className="rounded-md border border-divider bg-ground px-2 py-1.5 text-micro">
                <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                  {d.result ? (
                    <Badge tone={d.result === 'succeeded' ? 'block' : 'ok'}>{d.result}</Badge>
                  ) : null}
                  <span className="text-muted">{d.decided_date ?? 'undated'}</span>
                  {d.decision_ref ? <span className="font-mono">{d.decision_ref}</span> : null}
                  {d.tribunal ? <span className="text-muted">· {d.tribunal}</span> : null}
                </div>
                {d.mark_text ? <div>{d.mark_text}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Decisions — analogous past decisions, browsable on their own
// ---------------------------------------------------------------------------

function DecisionsPane({ office, ground }: { office: string | null; ground: string | null }) {
  const [result, setResult] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<string | null>(null)
  const decisions = useDecisions(office, ground, result, outcome, true)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Result">
        <FilterChip active={result === null} onClick={() => setResult(null)}>
          all results
        </FilterChip>
        {(['succeeded', 'failed'] as const).map((value) => (
          <FilterChip key={value} active={result === value} onClick={() => setResult(value)}>
            {value}
          </FilterChip>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Outcome">
        <FilterChip active={outcome === null} onClick={() => setOutcome(null)}>
          all outcomes
        </FilterChip>
        {(['refused', 'partial', 'survived'] as const).map((value) => (
          <FilterChip key={value} active={outcome === value} onClick={() => setOutcome(value)}>
            {value}
          </FilterChip>
        ))}
      </div>
      <p className="m-0 text-micro text-muted">
        <code>succeeded</code> means the objection succeeded — bad for the applicant. Most rows carry no
        source document: a rate, not a quotation.
      </p>
      <EmptyRows
        loading={decisions.isPending}
        error={decisions.error}
        count={decisions.data?.decisions.length ?? 0}
      >
        <CappedNote total={decisions.data?.total} capped={decisions.data?.capped} />
        <Table>
          <THead>
            <TR>
              <TH>Office</TH>
              <TH>Ground</TH>
              <TH>Result</TH>
              <TH>Outcome</TH>
              <TH>Decided</TH>
              <TH>Reference</TH>
              <TH>Tribunal</TH>
              <TH>Mark</TH>
            </TR>
          </THead>
          <TBody>
            {(decisions.data?.decisions ?? []).map((row) => (
              <TR key={row.id}>
                {/* `?? '—'` like every other cell here: a null office renders as
                    nothing at all in JSX, which reads as an empty column rather
                    than as a row the corpus has no office for. */}
                <TD>{row.office ?? '—'}</TD>
                <TD>{plainText(row.ground) ?? '—'}</TD>
                <TD>
                  {row.result ? <Badge tone={row.result === 'succeeded' ? 'block' : 'ok'}>{row.result}</Badge> : '—'}
                </TD>
                <TD>{row.outcome ?? '—'}</TD>
                <TD>{row.decided_date ?? '—'}</TD>
                <TD>
                  <span className="font-mono text-micro">{row.decision_ref ?? '—'}</span>
                </TD>
                <TD>{row.tribunal ?? '—'}</TD>
                <TD>{row.mark_text ?? '—'}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </EmptyRows>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Nice classes — the 45-class schedule, browsable, plus a grounded suggester
// ---------------------------------------------------------------------------

/** Milliseconds of quiet before a typed search actually reaches the server.
 * `tm_nice_class` is ~83,000 rows and `q` runs a COUNT plus an unindexed
 * case-insensitive scan over all of it, so keying the query on every
 * keystroke turned "downloadable software" into twenty of those — nineteen
 * for prefixes nobody asked about, each separately cached. */
const SEARCH_DEBOUNCE_MS = 300

function NiceClassesPane() {
  const [classNumber, setClassNumber] = useState<number | null>(null)
  const [q, setQ] = useState('')
  const [submittedQ, setSubmittedQ] = useState('')
  const classes = useNiceClasses(classNumber, submittedQ.trim() || null, true)

  useEffect(() => {
    const timer = setTimeout(() => setSubmittedQ(q), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [q])

  return (
    <div className="flex flex-col gap-4">
      <SuggestNiceClassTool />

      <div className="flex flex-col gap-2 border-t border-divider pt-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
          <Field label="Class number" hint="1-45">
            {({ id }) => (
              <Input
                id={id}
                type="number"
                min={1}
                max={45}
                value={classNumber ?? ''}
                onChange={(event) => setClassNumber(event.target.value ? Number(event.target.value) : null)}
              />
            )}
          </Field>
          <Field label="Search" hint="Substring match over the description">
            {({ id }) => <Input id={id} value={q} onChange={(event) => setQ(event.target.value)} />}
          </Field>
        </div>

        <EmptyRows loading={classes.isPending} error={classes.error} count={classes.data?.classes.length ?? 0}>
          <CappedNote total={classes.data?.total} capped={classes.data?.capped} />
          <Table>
            <THead>
              <TR>
                <TH align="right">Class</TH>
                <TH>Heading</TH>
                <TH>Description</TH>
              </TR>
            </THead>
            <TBody>
              {(classes.data?.classes ?? []).map((row) => (
                <TR key={row.id}>
                  <TD align="right">{row.class_number}</TD>
                  <TD>{row.class_heading}</TD>
                  <TD>{row.description}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </EmptyRows>
      </div>
    </div>
  )
}

function SuggestNiceClassTool() {
  const suggest = useSuggestNiceClasses()
  const [description, setDescription] = useState('')

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!description.trim()) return
    suggest.mutate({ description: description.trim() })
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <Field
        label="Suggest classes"
        hint="Describe the goods or services in your own words — every suggestion is checked against a candidate this search actually retrieved, never invented."
      >
        {({ id }) => (
          <Textarea
            id={id}
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Downloadable mobile app for tracking fitness activity"
          />
        )}
      </Field>
      <Button
        type="submit"
        variant="primary"
        loading={suggest.isPending}
        loadingLabel="Searching…"
        className="self-start"
      >
        Suggest
      </Button>
      {suggest.isError ? (
        <p role="alert" className="m-0 text-micro text-block-fg">
          {errorMessage(suggest.error)}
        </p>
      ) : null}
      {suggest.data ? <SuggestNiceClassResult response={suggest.data} /> : null}
    </form>
  )
}

function SuggestNiceClassResult({ response }: { response: SuggestNiceClassResponse }) {
  if (!response.semantic_search) {
    return (
      <p className="m-0 rounded-md bg-ground px-3 py-2 text-micro text-muted">
        No embedder is configured in this environment, so nothing could be ranked against the
        description — no completion was spent. Configure an embedder in Admin → Models &amp; keys to
        use this tool.
      </p>
    )
  }
  if (response.candidates_considered === 0) {
    return (
      <p className="m-0 rounded-md bg-ground px-3 py-2 text-micro text-muted">
        Nothing in the classification matched closely enough to check — no completion was spent.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-2 border-t border-divider pt-3">
      <p className="m-0 text-micro text-muted">
        {response.candidates_considered} candidate(s) considered.
      </p>
      {response.choices.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {response.choices.map((choice) => (
            <li
              key={choice.id}
              className="rounded-md border border-ok-line bg-ok-bg px-2 py-1.5 text-micro"
            >
              <div className="mb-1 flex items-center gap-2">
                <Badge tone="ok">class {choice.class_number}</Badge>
                <span className="font-medium">{choice.description}</span>
              </div>
              <p className="m-0 text-muted">{choice.reasoning}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-micro text-muted">
          None of the retrieved candidates were judged to fit this description.
        </p>
      )}
      {response.rejected.length > 0 ? (
        <p className="m-0 text-micro text-warn-fg">
          {response.rejected.length} claimed choice(s) named a candidate never shown and were
          discarded rather than trusted.
        </p>
      ) : null}
    </div>
  )
}

function EmptyRows({
  loading,
  error,
  count,
  children,
}: {
  loading: boolean
  error: unknown
  count: number
  children: React.ReactNode
}) {
  // `error` checked before `loading`/`count`: a query that has settled into
  // an error state reports `isPending: false` and its `.data` stays
  // `undefined`, which every call site's own `count` falls back to `0` for
  // — indistinguishable, before this, from a query that succeeded and
  // genuinely found nothing.
  if (error) {
    return (
      <p role="alert" className="m-0 text-body text-block-fg">
        {errorMessage(error)}
      </p>
    )
  }
  if (loading) return <p className="m-0 text-body text-muted">Loading…</p>
  if (count === 0) {
    return <p className="m-0 text-body text-muted">No rows for this office/ground/table combination.</p>
  }
  return <>{children}</>
}

/** "No silent caps" — a truncated answer says so, next to the total it was
 * truncated from, matching `corpus_law.py`'s own explorer copy. */
function CappedNote({ total, capped }: { total: number | undefined; capped: boolean | undefined }) {
  if (!capped) return null
  return (
    <p className="m-0 text-micro text-warn-fg">
      Showing a capped subset — {total} row(s) matched in total.
    </p>
  )
}

// ---------------------------------------------------------------------------
// Ask — a question, answered from the corpus, every claim quoted and verified
// ---------------------------------------------------------------------------

function AskWorkspace() {
  const ask = useAskCorpus()
  const [office, setOffice] = useState<Jurisdiction>('IN')
  const [groundsText, setGroundsText] = useState('')
  const [question, setQuestion] = useState('')

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const grounds = cleanGrounds(groundsText.split(','))
    if (!grounds.length || !question.trim()) return
    ask.mutate({ office, grounds, question: question.trim() })
  }

  return (
    <Card role="group" aria-label="Ask a question" className="gap-4">
      <CardHeader
        title="Ask a question"
        description="A question answered from the law library. Every claim is copied word for word from a named source and checked before you see it. A claim that fails the check is shown as rejected, never quietly dropped."
      />

      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
          <Field label="Office">
            {({ id }) => (
              <select
                id={id}
                className="input"
                value={office}
                onChange={(event) => setOffice(event.target.value as Jurisdiction)}
              >
                {JURISDICTIONS.map((code) => (
                  <option key={code} value={code}>
                    {jurisdictionLabel(code)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Grounds" hint="Comma-separated, e.g. 11(1), 9(2)">
            {({ id }) => (
              <Input
                id={id}
                value={groundsText}
                onChange={(event) => setGroundsText(event.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label="Question">
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              required
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="What do we argue for 2(d), and how often does it work?"
            />
          )}
        </Field>
        <Button
          type="submit"
          variant="primary"
          loading={ask.isPending}
          loadingLabel="Asking…"
          className="self-start"
        >
          Ask
        </Button>
        {ask.isError ? (
          <p role="alert" className="m-0 text-micro text-block-fg">
            {errorMessage(ask.error)}
          </p>
        ) : null}
      </form>

      {ask.data ? (
        <AskResult
          citations={ask.data.citations}
          rejected={ask.data.rejected}
          answer={ask.data.answer}
          sourcesConsidered={ask.data.sources_considered}
          vectorLed={ask.data.vector_led}
        />
      ) : null}
    </Card>
  )
}

function AskResult({
  answer,
  citations,
  rejected,
  sourcesConsidered,
  vectorLed,
}: {
  answer: string
  citations: AskCitation[]
  rejected: AskRejectedCitation[]
  sourcesConsidered: number
  vectorLed: boolean
}) {
  return (
    <div className="flex flex-col gap-3 border-t border-divider pt-3">
      <p className="m-0 whitespace-pre-wrap text-body">{answer}</p>
      <p className="m-0 text-micro text-muted">
        {sourcesConsidered} source(s) considered
        {vectorLed ? ', ranked by relevance to the question' : ', in unranked office/ground order'}.
      </p>

      {citations.length > 0 ? (
        <div>
          <h5 className="m-0 mb-1 text-meta">Citations ({citations.length})</h5>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {citations.map((c, i) => (
              <li
                key={`${c.custom_id}:${i}`}
                className="rounded-md border border-ok-line bg-ok-bg px-2 py-1.5 text-micro"
              >
                <div className="mb-1 flex items-center gap-2">
                  <Badge tone="neutral">{c.kind}</Badge>
                  <Badge tone={c.citability === 'citable' ? 'ok' : 'warn'}>
                    {c.citability === 'citable' ? 'citable' : 'internal only'}
                  </Badge>
                  <OpenSource customId={c.custom_id} />
                </div>
                &ldquo;{c.quote}&rdquo;
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {rejected.length > 0 ? (
        <div>
          <h5 className="m-0 mb-1 text-meta">Rejected ({rejected.length})</h5>
          <p className="m-0 mb-1 text-micro text-muted">
            Claimed but not verified — shown, not hidden, so a rejection is visible rather than a
            silently thinner answer.
          </p>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {rejected.map((r, i) => (
              <li
                key={`${r.custom_id}:${i}`}
                className="rounded-md border border-block-line bg-block-bg px-2 py-1.5 text-micro text-block-fg"
              >
                <Badge tone="block">{r.reason === 'unknown_source' ? 'unknown source' : 'quote not found'}</Badge>{' '}
                &ldquo;{r.quote}&rdquo; (claimed from <code className="font-mono">{r.custom_id}</code>)
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Statistics — the registered rates, every one with its caveat (I9)
// ---------------------------------------------------------------------------

function StatisticsWorkspace() {
  const [office, setOffice] = useState<string | null>(null)
  const [ground, setGround] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)

  const queries = useRegisteredQueries(office, ground)
  const result = useStatQuery(selected)
  const selectedQuery = (queries.data?.queries ?? []).find((q) => q.query_id === selected)

  return (
    <Card role="group" aria-label="Statistics" className="gap-4">
      <CardHeader
        title="Statistics"
        description="Every rate we publish, worked out the same way every time from recorded decisions and outcomes, with the caveat that belongs with it."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Office">
          {({ id }) => (
            <Input
              id={id}
              placeholder="all offices"
              value={office ?? ''}
              onChange={(event) => setOffice(event.target.value || null)}
            />
          )}
        </Field>
        <Field label="Ground">
          {({ id }) => (
            <Input
              id={id}
              placeholder="all grounds"
              value={ground ?? ''}
              onChange={(event) => setGround(event.target.value || null)}
            />
          )}
        </Field>
      </div>

      {queries.isPending ? <p className="m-0 text-body text-muted">Loading the catalogue…</p> : null}
      {queries.isError ? (
        <p role="alert" className="m-0 text-micro text-block-fg">
          {errorMessage(queries.error)}
        </p>
      ) : null}
      {queries.data && queries.data.queries.length === 0 ? (
        <p className="m-0 text-body text-muted">No registered statistic matches this filter.</p>
      ) : null}

      <Table>
        <THead>
          <TR>
            <TH>Statistic</TH>
            <TH>Basis</TH>
            <TH></TH>
          </TR>
        </THead>
        <TBody>
          {(queries.data?.queries ?? []).map((q: RegisteredQuery) => (
            <TR key={q.query_id} className={selected === q.query_id ? 'bg-brand-50' : undefined}>
              <TD>
                {/* What it measures leads; the registry id stays on the row, small,
                    for anyone who has to quote it to whoever maintains the registry. */}
                <div>{q.description}</div>
                <div className="font-mono text-micro text-muted" title="Statistic id">
                  {q.query_id}
                </div>
              </TD>
              <TD>{q.basis.replace('_', ' ')}</TD>
              <TD>
                <Button size="sm" variant="secondary" onClick={() => setSelected(q.query_id)}>
                  Run
                </Button>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>

      {selected ? (
        <div className="flex flex-col gap-1 border-t border-divider pt-3">
          {result.isPending ? (
            <p className="m-0 text-body text-muted">
              Running &ldquo;{selectedQuery?.description ?? selected}&rdquo;…
            </p>
          ) : null}
          {result.isError ? (
            <p role="alert" className="m-0 text-micro text-block-fg">
              {errorMessage(result.error)}
            </p>
          ) : null}
          {result.data ? (
            result.data.refused ? (
              <p className="m-0 rounded-md border border-block-line bg-block-bg px-3 py-2 text-body text-block-fg">
                {refusalCopy(result.data.refusal_reason, result.data.caveat)}
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                <p className="m-0 flex items-center gap-2 text-h2 font-semibold">
                  {result.data.rate !== null ? `${(result.data.rate * 100).toFixed(1)}%` : '—'}
                  {result.data.low_confidence ? (
                    <Badge tone="warn" className="text-micro font-normal">
                      Low confidence
                    </Badge>
                  ) : null}
                </p>
                <p className="m-0 text-micro text-muted">
                  {result.data.favourable} of {result.data.n}
                  {result.data.low_confidence
                    ? ' — too small a sample to read as a precise rate'
                    : ''}
                </p>
                <p className="m-0 rounded-md bg-ground px-2 py-1.5 text-micro text-muted">
                  {result.data.caveat}
                </p>
              </div>
            )
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Ledger — a matter's citations, across every run
// ---------------------------------------------------------------------------

function LedgerWorkspace() {
  const [input, setInput] = useState('')
  const [matterId, setMatterId] = useState<string | null>(null)
  const ledger = useMatterLedger(matterId)

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setMatterId(input.trim() || null)
  }

  return (
    <Card role="group" aria-label="Citations" className="gap-4">
      <CardHeader
        title="Citations"
        description="Each source any run on a matter relied on, listed once, with the run or runs it came from."
      />

      <form className="flex items-end gap-2" onSubmit={handleSubmit}>
        <Field label="Matter ID" className="flex-1">
          {({ id }) => (
            <Input id={id} mono value={input} onChange={(event) => setInput(event.target.value)} placeholder="GW-2026-0001" />
          )}
        </Field>
        <Button type="submit" variant="primary">
          Load
        </Button>
      </form>

      {matterId && ledger.isPending ? <p className="m-0 text-body text-muted">Loading…</p> : null}
      {matterId && ledger.isError ? (
        <p role="alert" className="m-0 text-micro text-block-fg">
          {errorMessage(ledger.error)}
        </p>
      ) : null}
      {ledger.data ? (
        ledger.data.entries.length === 0 ? (
          <p className="m-0 text-body text-muted">
            {ledger.data.run_count === 0
              ? 'This matter has never been run.'
              : "This matter's run(s) cited no sources."}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-micro text-muted">
              {ledger.data.entries.length} source(s) across {ledger.data.run_count} run(s).
            </p>
            <Table>
              <THead>
                <TR>
                  <TH>Source</TH>
                  <TH>Cite</TH>
                  <TH>Office</TH>
                  <TH>Runs</TH>
                  <TH>State</TH>
                </TR>
              </THead>
              <TBody>
                {ledger.data.entries.map((entry) => (
                  <TR key={entry.custom_id}>
                    <TD>
                      <span className="font-mono text-micro">{entry.custom_id}</span>
                    </TD>
                    <TD>{entry.cite ?? entry.title ?? '—'}</TD>
                    <TD>{entry.office ?? '—'}</TD>
                    <TD>{entry.run_ids.length}</TD>
                    <TD>
                      {entry.openable ? (
                        <OpenSource customId={entry.custom_id} />
                      ) : (
                        <Badge tone={entry.status === 'unusable' ? 'warn' : 'neutral'}>{entry.status}</Badge>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )
      ) : null}
    </Card>
  )
}
